import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import AdminLayout from '../components/admin/AdminLayout';
import {
  createGivingCycleAdmin,
  listGivingCyclesAdmin,
  setGivingCycleStatusAdmin,
  updateGivingCycleAdmin,
} from '../utils/givingApi';

const monthKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const monthLabel = (id) => {
  const [year, month] = id.split('-').map(Number);
  if (!year || !month) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' })
    .format(new Date(year, month - 1, 1));
};

const newNominee = () => ({ id: '', name: '', blurb: '', website: '' });

const emptyForm = () => {
  const id = monthKey();
  return {
    id,
    label: monthLabel(id),
    status: 'draft',
    startsAt: '',
    endsAt: '',
    nominees: [newNominee(), newNominee()],
  };
};

const toLocalDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const formFromCycle = (cycle) => ({
  id: cycle.id,
  label: cycle.label,
  status: cycle.status,
  startsAt: toLocalDateTime(cycle.startsAt),
  endsAt: toLocalDateTime(cycle.endsAt),
  nominees: cycle.nominees.map((nominee) => ({
    id: nominee.id,
    name: nominee.name,
    blurb: nominee.blurb || '',
    website: nominee.website || '',
  })),
});

const statusClasses = {
  active: 'bg-emerald-100 text-emerald-700',
  draft: 'bg-amber-100 text-amber-700',
  closed: 'bg-gray-100 text-gray-600',
};

const AdminGivingPage = () => {
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingID, setEditingID] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadCycles = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await listGivingCyclesAdmin();
      setCycles(data.cycles || []);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load giving cycles.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      const token = await auth.currentUser?.getIdTokenResult();
      const allowed = token?.claims?.admin === true;
      if (!active) return;
      setIsAdmin(allowed);
      setIsCheckingAdmin(false);
      if (allowed) await loadCycles();
    };
    initialize().catch((initializationError) => {
      if (!active) return;
      setError(initializationError.message || 'Unable to verify admin access.');
      setIsCheckingAdmin(false);
    });
    return () => { active = false; };
  }, [loadCycles]);

  const activeCycle = useMemo(() => cycles.find((cycle) => cycle.status === 'active'), [cycles]);

  const updateForm = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const updateNominee = (index, key, value) => {
    setForm((previous) => ({
      ...previous,
      nominees: previous.nominees.map((nominee, nomineeIndex) =>
        nomineeIndex === index ? { ...nominee, [key]: value } : nominee
      ),
    }));
  };

  const resetForm = () => {
    setEditingID(null);
    setForm(emptyForm());
    setError('');
    setNotice('');
  };

  const saveCycle = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = {
        ...form,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      };
      if (editingID) {
        await updateGivingCycleAdmin(editingID, payload);
        setNotice(`${form.label} updated.`);
      } else {
        await createGivingCycleAdmin(payload);
        setNotice(`${form.label} created.`);
      }
      setEditingID(null);
      setForm(emptyForm());
      await loadCycles();
    } catch (saveError) {
      setError(saveError.message || 'Unable to save the cycle.');
    } finally {
      setIsSaving(false);
    }
  };

  const changeStatus = async (cycle, status) => {
    const message = status === 'active'
      ? `Activate ${cycle.label}? Any other active cycle will be closed.`
      : `${status === 'closed' ? 'Close' : 'Move'} ${cycle.label} ${status === 'closed' ? '' : 'to draft'}?`;
    if (!window.confirm(message)) return;
    setError('');
    setNotice('');
    try {
      await setGivingCycleStatusAdmin(cycle.id, status);
      setNotice(`${cycle.label} is now ${status}.`);
      await loadCycles();
    } catch (statusError) {
      setError(statusError.message || 'Unable to change cycle status.');
    }
  };

  if (isCheckingAdmin) {
    return <AdminLayout><div className="p-6 text-gray-600">Checking admin access…</div></AdminLayout>;
  }
  if (!isAdmin) {
    return <AdminLayout><div className="p-6 text-red-600">You need admin access to manage Giving.</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Giving Admin</h1>
            <p className="mt-0.5 text-sm text-gray-500">Create monthly votes and monitor private results.</p>
          </div>
          <button
            type="button"
            onClick={loadCycles}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isLoading ? 'Refreshing…' : 'Refresh results'}
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {notice && <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>}

        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Active cycle</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">{activeCycle?.label || 'None'}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Active votes</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">{activeCycle?.totalVotes ?? 0}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Leading charity</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {activeCycle?.nominees.find(({ id }) => id === activeCycle.leaderId)?.name || '—'}
            </div>
          </div>
        </div>

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(360px,0.85fr)_minmax(500px,1.15fr)]">
          <form onSubmit={saveCycle} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-gray-900">{editingID ? 'Edit cycle' : 'Create monthly cycle'}</h2>
                <p className="mt-1 text-xs text-gray-500">The public apps receive no vote totals.</p>
              </div>
              {editingID && <button type="button" onClick={resetForm} className="text-sm text-blue-600 hover:underline">Cancel edit</button>}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm text-gray-700">
                Month
                <input
                  type="month"
                  required
                  disabled={Boolean(editingID)}
                  value={form.id}
                  onChange={(event) => {
                    updateForm('id', event.target.value);
                    updateForm('label', monthLabel(event.target.value));
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-100"
                />
              </label>
              <label className="text-sm text-gray-700">
                Status
                <select value={form.status} onChange={(event) => updateForm('status', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
            </div>

            <label className="mt-4 block text-sm text-gray-700">
              Display label
              <input required maxLength={100} value={form.label} onChange={(event) => updateForm('label', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
            </label>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="text-sm text-gray-700">
                Voting opens (optional)
                <input type="datetime-local" value={form.startsAt} onChange={(event) => updateForm('startsAt', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              </label>
              <label className="text-sm text-gray-700">
                Voting closes (optional)
                <input type="datetime-local" value={form.endsAt} onChange={(event) => updateForm('endsAt', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              </label>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Charities</h3>
              <button type="button" onClick={() => updateForm('nominees', [...form.nominees, newNominee()])} className="text-sm font-medium text-blue-600 hover:underline">Add charity</button>
            </div>

            <div className="mt-3 space-y-3">
              {form.nominees.map((nominee, index) => (
                <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Charity {index + 1}</div>
                    {form.nominees.length > 2 && (
                      <button type="button" onClick={() => updateForm('nominees', form.nominees.filter((_, itemIndex) => itemIndex !== index))} className="text-xs text-red-600 hover:underline">Remove</button>
                    )}
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <input required maxLength={100} placeholder="Name" value={nominee.name} onChange={(event) => updateNominee(index, 'name', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    <input maxLength={64} placeholder="ID (auto from name)" value={nominee.id} onChange={(event) => updateNominee(index, 'id', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  </div>
                  <textarea maxLength={300} rows={2} placeholder="Short description" value={nominee.blurb} onChange={(event) => updateNominee(index, 'blurb', event.target.value)} className="mt-2 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  <input type="url" maxLength={500} placeholder="Website (optional)" value={nominee.website} onChange={(event) => updateNominee(index, 'website', event.target.value)} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
              ))}
            </div>

            <button disabled={isSaving} type="submit" className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {isSaving ? 'Saving…' : (editingID ? 'Save changes' : 'Create cycle')}
            </button>
          </form>

          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Monthly cycles</h2>
              <span className="text-xs text-gray-500">Admin-only totals</span>
            </div>
            {!isLoading && cycles.length === 0 && <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">No giving cycles yet.</div>}
            {cycles.map((cycle) => {
              const maximum = Math.max(1, ...Object.values(cycle.voteCounts || {}));
              return (
                <article key={cycle.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{cycle.label}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${statusClasses[cycle.status]}`}>{cycle.status}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">{cycle.id} · {cycle.totalVotes} total votes</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => { setEditingID(cycle.id); setForm(formFromCycle(cycle)); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Edit</button>
                      {cycle.status !== 'active' && <button type="button" onClick={() => changeStatus(cycle, 'active')} className="rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700">Activate</button>}
                      {cycle.status === 'active' && <button type="button" onClick={() => changeStatus(cycle, 'closed')} className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Close voting</button>}
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {cycle.nominees.map((nominee) => {
                      const count = cycle.voteCounts?.[nominee.id] || 0;
                      const isLeader = cycle.leaderId === nominee.id;
                      return (
                        <div key={nominee.id}>
                          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                            <div className="font-medium text-gray-800">{nominee.name}{isLeader && <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">Leading</span>}</div>
                            <div className="tabular-nums text-gray-600">{count}</div>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                            <div className={`h-full rounded-full ${isLeader ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${(count / maximum) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </section>
        </div>
      </main>
    </AdminLayout>
  );
};

export default AdminGivingPage;
